import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GetSubAdmin, UpdateSubAdminPermissions, Permission } from '../../api/api';
import { useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table/index';
import toast from 'react-hot-toast';

const RESOURCES = [
  { value: 'families', label: 'Families' },
  { value: 'family-members', label: 'Family Members' },
  { value: 'units', label: 'Units' },
  { value: 'events', label: 'Events' },
  { value: 'news', label: 'News' },
  { value: 'history', label: 'History' },
  { value: 'hero-images', label: 'Hero Images' },
  { value: 'users', label: 'Users' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'faq', label: 'FAQ' },
  { value: 'settings', label: 'Settings' },
];

const ACTIONS = [
  { value: 'read', label: 'Read' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
];

const SubAdminPermissions: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMainAdmin = useIsMainAdmin();
  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);
  const [subAdmin, setSubAdmin] = useState<any>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Wait for permissions to be fetched before checking
    if (isLoadingPermissions || !hasFetched) {
      return; // Wait for permissions to load
    }

    if (!isMainAdmin) {
      toast.error('Only main admin can access this page');
      navigate('/');
      return;
    }
    if (id) {
      fetchSubAdmin();
    }
  }, [id, isMainAdmin, isLoadingPermissions, hasFetched, navigate]);

  const fetchSubAdmin = async () => {
    setLoading(true);
    const { data, error } = await GetSubAdmin<{ data: { subAdmin: any } }>(id!);
    if (!error && data?.data) {
      setSubAdmin(data.data.subAdmin);
      setPermissions(data.data.subAdmin.permissions || []);
    }
    setLoading(false);
  };

  const toggleAction = (resource: string, action: string) => {
    setPermissions((prev) => {
      const resourcePerm = prev.find((p) => p.resource === resource);
      if (resourcePerm) {
        const hasAction = resourcePerm.actions.includes(action);
        if (hasAction) {
          // Remove action
          const newActions = resourcePerm.actions.filter((a) => a !== action);
          if (newActions.length === 0) {
            // Remove entire permission if no actions left
            return prev.filter((p) => p.resource !== resource);
          }
          return prev.map((p) => (p.resource === resource ? { ...p, actions: newActions } : p));
        } else {
          // Add action
          return prev.map((p) =>
            p.resource === resource ? { ...p, actions: [...p.actions, action] } : p
          );
        }
      } else {
        // Create new permission
        return [...prev, { resource, actions: [action] }];
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await UpdateSubAdminPermissions(id!, { permissions });
    if (!error) {
      toast.success('Permissions updated successfully');
      navigate('/sub-admins');
    }
    setSaving(false);
  };

  // Show loading while permissions are being fetched
  if (isLoadingPermissions || !hasFetched) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading permissions...</div>
      </div>
    );
  }

  if (!isMainAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Manage Permissions" description="Set specific permissions for sub-admin" />
      <div className="p-6">
        <PageBreadcrumb pageTitle="Manage Permissions" />

        <ComponentCard
          title="Manage Permissions"
          desc={`${subAdmin?.name || subAdmin?.email} - Set specific permissions for this sub-admin`}
        >
          <div className="overflow-x-auto">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
              <Table>
                <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                  <TableRow>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      Resource
                    </TableCell>
                    {ACTIONS.map((action) => (
                      <TableCell
                        key={action.value}
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        {action.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {RESOURCES.map((resource) => {
                    const resourcePerm = permissions.find((p) => p.resource === resource.value);
                    return (
                      <TableRow key={resource.value} className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableCell className="px-5 py-4 text-sm font-medium text-gray-900 dark:text-white/90" style={{ fontFamily: 'Lato, sans-serif' }}>
                          {resource.label}
                        </TableCell>
                        {ACTIONS.map((action) => {
                          const hasAction = resourcePerm?.actions.includes(action.value) || false;
                          return (
                            <TableCell key={action.value} className="px-5 py-4 text-center">
                              <input
                                type="checkbox"
                                checked={hasAction}
                                onChange={() => toggleAction(resource.value, action.value)}
                                className="w-5 h-5 rounded focus:ring-2 focus:ring-[#B8860B] text-[#B8860B] border-gray-300"
                                style={{ accentColor: '#B8860B' }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <button
              type="button"
              onClick={() => navigate('/sub-admins')}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center justify-center"
              style={{ fontFamily: 'Lato, sans-serif', height: '36px', minWidth: '100px' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              style={{ fontFamily: 'Lato, sans-serif', backgroundColor: '#B8860B', height: '36px', minWidth: '140px' }}
            >
              {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </ComponentCard>
      </div>
    </>
  );
};

export default SubAdminPermissions;
